import { useEffect, useState, type FC } from 'react';
import {
  Button,
  RadioButtonGroup,
  RadioButton,
  Tag,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { formatNumber } from 'utils/number';
import { formatMutationResource } from 'utils/misc';
import { formatTcWorkspaceDate } from 'utils/dates';
import { preDrillSgcQueryAtom } from './atoms';
import styles from './sgc-picker-drawer.module.scss';

interface SgcPickerDrawerProps {
  uwi14: string;
  selectedId: number | null;
  saving: boolean;
  onClose: () => void;
  onSelect: (collectionId: number) => void;
}

// Structure Grid Collection picker (right drawer): lists collections
// intersecting the subject permit; the pending selection PATCHes on Apply.
export const SgcPickerDrawer: FC<SgcPickerDrawerProps> = ({
  uwi14,
  selectedId,
  saving,
  onClose,
  onSelect,
}) => {
  const [resource, fetchCollections] = useAtom(preDrillSgcQueryAtom);
  const [pendingId, setPendingId] = useState<number | null>(selectedId);

  useEffect(() => {
    fetchCollections({
      type: 'POST',
      url: '/queries/structure_grid_collections',
      data: { query: { intersects: { uwi14 } } },
    });
  }, [uwi14, fetchCollections]);

  const { loading, data, error } = formatMutationResource(resource);
  const rows = data?.results ?? [];

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Structure Grid Collection</h2>
          <p className={styles.subtitle}>
            Pick which grid drives the geometry sections. Only collections that
            intersect this wellbore are shown.
          </p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        {!loading && error?.type && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Failed to load collections"
          />
        )}
        {loading && !data && (
          <InlineLoading description="Loading collections…" />
        )}
        {!loading && !error?.type && rows.length === 0 && (
          <p className={styles.empty}>
            No structure grid collections intersect this permit.
          </p>
        )}

        {rows.length > 0 && (
          <RadioButtonGroup
            name="structure-grid-collection"
            valueSelected={pendingId === null ? '' : String(pendingId)}
            orientation="vertical"
            legendText=""
            onChange={(value) => setPendingId(Number(value))}
          >
            {rows.map((row) => (
              <RadioButton
                key={row.id}
                id={`grid-collection-${row.id}`}
                value={String(row.id)}
                labelText={
                  <span className={styles.optionLabel}>
                    <span className={styles.optionName}>
                      {row.collectionName}
                      {row.isDefault && (
                        <Tag type="gray" size="sm">
                          Default
                        </Tag>
                      )}
                    </span>
                    <span className={styles.optionMeta}>
                      Basin: {row.basin} · {formatNumber(row.intervals, 0, 0)}{' '}
                      intervals · Owner: {row.owner}
                    </span>
                    <span className={styles.optionMetaMuted}>
                      {row.status} · {row.validity} · Auto-update landing zone:{' '}
                      {row.landingAutoUpdateEnabled ? 'Yes' : 'No'} · Created{' '}
                      {formatTcWorkspaceDate(row.sourceCollectionCreatedAt)}
                    </span>
                  </span>
                }
              />
            ))}
          </RadioButtonGroup>
        )}
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="md"
          disabled={pendingId === null || saving}
          onClick={() => {
            if (pendingId !== null) onSelect(pendingId);
          }}
        >
          {saving ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
};
